// PATH: src/components/settings/llmSettings.ts
/**
 * Réglages IA par matière (bloc `llm` de l'app-config `tilmid-plus`) : logique
 * pure. La règle d'héritage est celle du serveur (`domain.ResolveLLM`) : champ
 * par champ, la matière l'emporte, le global sinon.
 */
import type { AudioFormat, LlmRole, LlmRoleSet, LlmRoleSettings, LlmSettings } from "../../models/domain";

export type LlmField =
  | "provider"
  | "model"
  | "temperature"
  | "reasoning_effort"
  | "instructions"
  | "voice"
  | "audio_format"
  | "fallback_model";

export interface RoleMeta {
  role: LlmRole;
  label: string;
  /** Une phrase : ce que fait ce rôle. */
  hint: string;
  /** Sous « Modèle » : ce que vaut le global, ce que le rôle demande au modèle (règles du serveur). */
  modelHint: string;
  /** Pour un rôle sans température réglable : ce qu'il envoie. */
  temperatureNote?: string;
  /** Température réglable (si le modèle l'accepte). */
  temperature: boolean;
  instructions: boolean;
  /** Interrupteur global (relectures), et ce que sa coupure fait aux élèves. */
  switchOff?: string;
  /** Réglé au global seulement (compagnon, OCR, voix) : absent du tableau par matière. */
  globalOnly?: boolean;
  /** Le service expose son prompt de base (objectifs, faits, quêtes). */
  basePrompt: boolean;
  /** Sous la carte : ce qu'il faut savoir en plus. */
  footnote?: string;
  /**
   * Aucun coût estimé à l'écran : l'OCR n'est pas tracé dans `ai_call`, et la
   * voix, tracée, n'est pas chiffrée par `cost-estimates` (payée à la minute,
   * aux caractères ou aux jetons audio, sans unité commune avec les autres).
   */
  noEstimate?: boolean;
  /** Champs de la voix (nom de voix, format audio) : lecture à voix haute seulement. */
  voiceFields?: boolean;
  /** Modèle de repli, appelé quand le principal échoue avant le premier son : lecture à voix haute seulement. */
  fallback?: boolean;
}

const GLOBAL_MODEL = "Valeur de toutes les matières sans écart propre.";

/** Ce que chaque rôle sait recevoir : le serveur refuse le reste. L'ordre est celui des cartes. */
export const ROLES: RoleMeta[] = [
  {
    role: "objective_extraction",
    label: "Objectifs",
    hint: "Tire de chaque leçon d'un chapitre importé ses objectifs, les relit, et reconnaît ceux qui existaient déjà pour garder la progression des élèves.",
    modelHint: `${GLOBAL_MODEL} Le même modèle extrait, relit et apparie ; il doit faire le JSON strict et accepter la température 0.`,
    temperatureNote: "0, toujours : les mêmes objectifs d'une publication à l'autre, donc la progression gardée.",
    temperature: false,
    instructions: true,
    basePrompt: true,
  },
  {
    role: "fact_extraction",
    label: "Extraction des faits",
    hint: "Tire du cours les faits (définitions, règles, exemples) de chaque objectif.",
    modelHint: `${GLOBAL_MODEL} Mode JSON et température 0 exigés.`,
    temperatureNote: "0, toujours : les mêmes faits d'une publication à l'autre.",
    temperature: false,
    instructions: true,
    basePrompt: true,
  },
  {
    role: "fact_review",
    label: "Relecture des faits",
    hint: "Vérifie chaque fait contre le cours et écarte ceux qu'il ne soutient pas.",
    modelHint: `${GLOBAL_MODEL} Mode JSON et température 0 exigés.`,
    temperatureNote: "0, toujours : un verdict doit être reproductible.",
    temperature: false,
    instructions: true,
    switchOff: "Les faits extraits ensuite seront servis sans vérification contre le cours.",
    basePrompt: true,
  },
  {
    role: "quest_generation",
    label: "Génération des quêtes",
    hint: "Rédige les quêtes (énoncés, choix, corrections, accroches) à partir des faits.",
    modelHint: `${GLOBAL_MODEL} JSON strict et flux exigés ; le générateur envoie toujours une température.`,
    temperature: true,
    instructions: true,
    basePrompt: true,
  },
  {
    role: "quest_review",
    label: "Relecture des quêtes",
    hint: "Relit la clé et la vérité de chaque quête avant qu'elle soit servie.",
    modelHint: `${GLOBAL_MODEL} Mode JSON exigé ; un modèle à raisonnement est admis.`,
    temperatureNote: "Réglée par le relecteur lui-même, selon le modèle.",
    temperature: false,
    instructions: false,
    switchOff:
      "Des quêtes non relues (clé, vérité des textes) seront servies aux élèves. Elles ne seront pas partagées entre élèves.",
    basePrompt: true,
  },
  {
    role: "course_writing",
    label: "Rédaction des cours",
    hint: "Rédige les chapitres de cours leçon par leçon dans l'atelier tc_docsourcing, les relit, les corrige et dépouille les repères d'un cours d'enseignant.",
    modelHint: `${GLOBAL_MODEL} Mode JSON exigé ; un modèle à raisonnement est admis (la passerelle retire la température). L'opérateur peut forcer un autre modèle déclaré au lancement (--modele).`,
    temperatureNote: "Fixée par l'atelier : 0,4 pour rédiger, 0 pour relire, 0,3 pour corriger.",
    temperature: false,
    instructions: true,
    basePrompt: true,
    noEstimate: true,
    footnote:
      "Pris en compte au lancement suivant de l'atelier. Chaque appel est tracé dans ai_call (service tc_docsourcing : rédaction, relecture, repères) ; pas d'estimation par unité ici.",
  },
  {
    role: "companion",
    label: "Compagnon",
    hint: "Répond à l'élève dans le chat, à partir de l'écran qu'il a devant lui.",
    modelHint:
      "Le même pour toutes les matières. Flux exigé, et un fournisseur autorisé à recevoir les messages des élèves (mineurs).",
    temperature: true,
    instructions: false,
    globalOnly: true,
    basePrompt: false,
    footnote: "Pris en compte au message suivant. Ses consignes vivent avec son code (tc_chat_ai).",
  },
  {
    role: "ocr",
    label: "Lecture des pages scannées (OCR)",
    hint: "Lit le texte des pages scannées d'un document importé, avant toute extraction.",
    modelHint:
      "Le même pour toutes les matières. Un modèle qui lit les images, ou un modèle d'OCR dédié (ex. Mistral OCR).",
    temperatureNote: "0, toujours : la page telle qu'elle est.",
    temperature: false,
    instructions: false,
    globalOnly: true,
    basePrompt: false,
    noEstimate: true,
    footnote: "Ses appels (worker tc_embedding) ne sont pas tracés : aucun coût estimé.",
  },
  {
    role: "tts",
    label: "Voix du Compagnon (lecture à voix haute)",
    hint: "Lit à l'élève, à voix haute, les accroches des quêtes et les réponses du compagnon dans le chat.",
    modelHint:
      "Le même pour toutes les matières. Un modèle de synthèse vocale (déclaré tts), chez un fournisseur autorisé à recevoir les données des élèves (le texte lu leur est adressé, prénom compris). La langue de lecture n'est pas un réglage : elle suit l'écran de l'élève.",
    temperature: false,
    instructions: false,
    globalOnly: true,
    basePrompt: false,
    noEstimate: true,
    voiceFields: true,
    fallback: true,
    footnote:
      "Pris en compte à la lecture suivante. Chaque lecture est tracée dans ai_call, au tarif du modèle (aux jetons ou au million de caractères) ; pas d'estimation ici.",
  },
  {
    role: "stt",
    label: "Écoute du Compagnon (dictée)",
    hint: "Transcrit ce que l'élève dit au micro, pour l'envoyer au compagnon comme un message écrit.",
    modelHint:
      "Le même pour toutes les matières. Un modèle de transcription (déclaré stt), chez un fournisseur autorisé à recevoir les données des élèves (leur voix lui est envoyée).",
    temperature: false,
    instructions: false,
    globalOnly: true,
    basePrompt: false,
    noEstimate: true,
    footnote:
      "Pris en compte à la dictée suivante. Chaque dictée est tracée dans ai_call, au tarif du modèle (à la minute d'audio ou aux jetons) ; pas d'estimation ici.",
  },
];

/** Les formats que la lecture à voix haute sait rendre (tc_chat_be les étiquette en type MIME). */
export const AUDIO_FORMATS: AudioFormat[] = ["mp3", "opus", "aac", "flac", "wav"];

/**
 * Les formats possibles quand la voix passe par un modèle temps réel (en
 * principal ou en repli) : il rend du son brut, que la passerelle encode en
 * mp3 ou enveloppe en wav, et rien d'autre.
 */
export const audioFormatsFor = (realtime: boolean): AudioFormat[] =>
  realtime ? AUDIO_FORMATS.filter((f) => f === "mp3" || f === "wav") : AUDIO_FORMATS;

/** Nom de voix du fournisseur (« marin », « cedar ») : la règle de structure du serveur. */
export const validVoice = (v: string) => /^[a-z0-9][a-z0-9_-]{0,39}$/.test(v);

/** Les rôles du tableau par matière. */
export const SUBJECT_ROLES = ROLES.filter((r) => !r.globalOnly);

/** La valeur posée à CE niveau (`undefined` = non posée). `subjectId` vide = le global. */
export const ownValue = (llm: LlmSettings, subjectId: string, role: LlmRole, field: LlmField) => {
  const set = subjectId ? llm.subjects?.[subjectId] : llm.global;
  const v = set?.[role]?.[field];
  return v === "" ? undefined : v;
};

/** La valeur effective et son origine : ce que le service utilisera vraiment. */
export const effectiveValue = (llm: LlmSettings, subjectId: string, role: LlmRole, field: LlmField) => {
  const own = subjectId ? ownValue(llm, subjectId, role, field) : undefined;
  if (own !== undefined) return { value: own, inherited: false };
  return { value: ownValue(llm, "", role, field), inherited: Boolean(subjectId) };
};

/**
 * Pose (ou retire, avec `undefined` ou "") un champ à un niveau. Une cellule
 * vidée revient à « hérite du global » ; un rôle ou une matière sans plus
 * aucun écart disparaissent du JSON — le serveur fait la même normalisation.
 */
export const setValue = (
  llm: LlmSettings,
  subjectId: string,
  role: LlmRole,
  field: LlmField,
  value: string | number | undefined,
): LlmSettings => {
  const clean = typeof value === "string" ? value.trim() || undefined : value;
  const level: LlmRoleSet = { ...(subjectId ? llm.subjects?.[subjectId] : llm.global) };
  const cell: LlmRoleSettings = { ...level[role] };
  if (clean === undefined) delete cell[field];
  else (cell as Record<LlmField, string | number>)[field] = clean;
  if (Object.keys(cell).length) level[role] = cell;
  else delete level[role];

  if (!subjectId) return { ...llm, global: level };
  const subjects = { ...llm.subjects };
  if (Object.keys(level).length) subjects[subjectId] = level;
  else delete subjects[subjectId];
  return { ...llm, subjects };
};

/** « Retirer » une matière : tous ses écarts disparaissent, elle revient au global. */
export const removeSubject = (llm: LlmSettings, subjectId: string): LlmSettings => {
  const subjects = { ...llm.subjects };
  delete subjects[subjectId];
  return { ...llm, subjects };
};

/** Le réglage GLOBAL d'un rôle a-t-il changé depuis l'enregistrement ? */
export const globalRoleChanged = (form: LlmSettings, saved: LlmSettings, role: LlmRole) =>
  stable(form.global[role] ?? {}) !== stable(saved.global[role] ?? {});

/** L'interrupteur d'une relecture (global). Absent = active, comme côté serveur. */
export const reviewEnabled = (llm: LlmSettings, role: LlmRole) => llm.global[role]?.enabled !== false;

export const setReviewEnabled = (llm: LlmSettings, role: LlmRole, enabled: boolean): LlmSettings => ({
  ...llm,
  global: { ...llm.global, [role]: { ...llm.global[role], enabled } },
});

const QUEST_FIELDS: [LlmRole, LlmField][] = [
  ["quest_generation", "model"],
  ["quest_generation", "instructions"],
  ["quest_review", "model"],
];
const FACT_FIELDS: [LlmRole, LlmField][] = [
  ["fact_extraction", "model"],
  ["fact_extraction", "instructions"],
];
const OBJECTIVE_FIELDS: [LlmRole, LlmField][] = [
  ["objective_extraction", "model"],
  ["objective_extraction", "instructions"],
];

const changed = (a: LlmSettings, b: LlmSettings, subjectId: string, fields: [LlmRole, LlmField][]) =>
  fields.some(([role, field]) => effectiveValue(a, subjectId, role, field).value !== effectiveValue(b, subjectId, role, field).value);

/**
 * Ce qu'un enregistrement va périmer. Le modèle et la consigne du générateur,
 * et le modèle du relecteur, entrent dans la clé des quêtes partagées (tc_learn) :
 * les changer pour une matière fait régénérer ses quêtes partagées. Le modèle et
 * la consigne de l'extraction ré-extraient ses faits au prochain run. Le modèle
 * et la consigne des objectifs ne périment RIEN : ils valent pour le prochain
 * import ou la prochaine extraction demandée d'un chapitre de la matière.
 *
 * Une matière est touchée si sa valeur EFFECTIVE change — un changement du
 * global touche donc toutes les matières qui en héritent, et aucune autre.
 */
export const impact = (saved: LlmSettings, form: LlmSettings, subjectIds: string[]) => ({
  quests: subjectIds.filter((id) => changed(saved, form, id, QUEST_FIELDS)),
  facts: subjectIds.filter((id) => changed(saved, form, id, FACT_FIELDS)),
  objectives: subjectIds.filter((id) => changed(saved, form, id, OBJECTIVE_FIELDS)),
  questsGlobal: changed(saved, form, "", QUEST_FIELDS),
  factsGlobal: changed(saved, form, "", FACT_FIELDS),
  objectivesGlobal: changed(saved, form, "", OBJECTIVE_FIELDS),
});

/** Comparaison stable (ordre des clés indifférent) : « y a-t-il quelque chose à enregistrer ? ». */
export const sameSettings = (a: LlmSettings, b: LlmSettings) => stable(a) === stable(b);

const stable = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${k}:${stable(o[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v);
};
